import { Component, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NbDialogRef, NbDialogService, NbCardModule } from "@nebular/theme";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { KPI } from "../../../@core/api";
import { AssetsService } from "../../../@core/utils";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-asset-photo-carousel-dialog",
  templateUrl: "./asset-photo-carousel-dialog.component.html",
  styleUrls: ["./asset-photo-carousel-dialog.component.scss"],
  standalone: true,
  imports: [CommonModule, NbCardModule, TranslateModule, ConfirmationDialogComponent]
})
export class AssetPhotoCarouselDialogComponent implements OnDestroy {
  public asset: KPI.MappedAsset;
  public pictures: KPI.AssetPicture[] = [];
  public currentIndex: number = 0;
  public isUploadingPhoto: boolean = false;
  public failedPictureIds: Set<number> = new Set();
  private _pendingCounter: number = 0;
  private _getLocalId(): string {
    return `${Date.now()}-${(++this._pendingCounter).toString(36)}`;
  }

  @ViewChild("photoFileInput") private photoFileInput: ElementRef<HTMLInputElement>;

  private subscriptions: Subscription[] = [];

  constructor(
    private dialogRef: NbDialogRef<AssetPhotoCarouselDialogComponent>,
    private dialogService: NbDialogService,
    private assetsService: AssetsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    const context = (this.dialogRef as any).context || {};
    this.asset = context.asset;
    this.pictures = [...(context.pictures || [])];
    this.currentIndex = context.initialIndex ?? 0;
  }

  public get currentPicture(): KPI.AssetPicture | null {
    return this.pictures[this.currentIndex] || null;
  }

  public prevPhoto(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  public nextPhoto(): void {
    if (this.currentIndex < this.pictures.length - 1) {
      this.currentIndex++;
    }
  }

  public selectPhoto(index: number): void {
    this.currentIndex = index;
  }

  public getThumbnailUrl(picture: KPI.AssetPicture): string | null {
    if (!picture?.thumbnail) return null;
    return picture.thumbnail.trim().replace(/["']/g, "") || null;
  }

  public onImageError(event: any, picture: KPI.AssetPicture): void {
    const cleanedThumbnail = this.getThumbnailUrl(picture);
    if (event.target.src === cleanedThumbnail && picture.picture !== cleanedThumbnail) {
      event.target.src = picture.picture;
    } else {
      this.failedPictureIds.add(picture.id);
      this.cdr.detectChanges();
    }
  }

  public triggerPhotoUpload(): void {
    this.photoFileInput?.nativeElement.click();
  }

  private resizeImage(file: File, maxWidth = 720, maxHeight = 1080): Promise<Blob> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => resolve(blob ?? file), file.type, 0.85);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  public onPhotoFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.isUploadingPhoto = true;

    const localId = this._getLocalId();
    const localUrl = URL.createObjectURL(file);
    const optimisticPicture: KPI.AssetPicture = { id: localId as any, picture: localUrl, thumbnail: null, asset: this.asset.id } as any;
    this.pictures = [...this.pictures, optimisticPicture];
    this.currentIndex = this.pictures.length - 1;
    this.cdr.detectChanges();

    this.resizeImage(file).then(blob => {
      const resized = new File([blob], file.name, { type: file.type });
      const formData = new FormData();
      formData.append("picture", resized);
      formData.append("asset", String(this.asset.id));
      formData.append("local_id", localId);

      this.subscriptions.push(
        this.assetsService.createAssetPicture(formData).subscribe({
          next: newPicture => {
            URL.revokeObjectURL(localUrl);
            this.pictures = this.pictures.map(p => (p.id as any) === localId ? newPicture : p);
            this.currentIndex = this.pictures.findIndex(p => p.id === newPicture.id);
            this.isUploadingPhoto = false;
            if (this.photoFileInput) {
              this.photoFileInput.nativeElement.value = "";
            }
          },
          error: () => {
            URL.revokeObjectURL(localUrl);
            this.pictures = this.pictures.filter(p => (p.id as any) !== localId);
            this.currentIndex = Math.max(0, this.pictures.length - 1);
            this.isUploadingPhoto = false;
            this.cdr.detectChanges();
          }
        })
      );
    });
  }

  public deleteCurrentPhoto(): void {
    const picture = this.currentPicture;
    if (!picture) return;

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      context: {
        title: this.translate.instant("asset_form_drawer.synthese.delete_photo_title"),
        label: this.translate.instant("asset_form_drawer.synthese.delete_photo_confirm")
      }
    });

    this.subscriptions.push(
      confirmDialog.onClose.subscribe(confirmed => {
        if (confirmed === true) {
          this.subscriptions.push(
            this.assetsService.deleteAssetPicture(picture.id).subscribe({
              next: () => {
                this.pictures = this.pictures.filter(p => p.id !== picture.id);
                this.currentIndex = Math.min(this.currentIndex, this.pictures.length - 1);
                if (this.currentIndex < 0) this.currentIndex = 0;
              }
            })
          );
        }
      })
    );
  }

  public close(): void {
    this.dialogRef.close(this.pictures);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
